/**
 * UI entry point: mount, per-frame update, teardown.
 *
 * Three layers, in z order:
 *   regions— the HUD, the run feed, the world prompts. Never interactive
 *            (clicks fall through to the canvas), and never positioned by hand:
 *            every occupant claims a named area from `hudLayout.ts` (#52).
 *   modal  — one full-screen panel at a time, interactive
 *   overlay— the hold-Tab radial and the prologue, both full-bleed
 *
 * The region layer is created by `hudLayout.ts` on the first claim and inserted
 * as the *first* child, which is what keeps the modal layer painting over it.
 * The overlay layer sits above both on purpose: the radial and the prologue
 * replace the picture rather than sharing it.
 *
 * Pointer-events are set *inline* rather than in the stylesheet because
 * index.html forces `#ui-root > * { pointer-events: auto }` on every direct
 * child, and an inline style is the only thing that reliably beats an id
 * selector without an !important arms race.
 *
 * Screens are UI-local navigation: the panel asks, UIRoot moves, and the
 * integrator is *told* via `onScreenChange` so it can decide about pausing.
 * Panels never pause anything themselves.
 */

import { CodexPanel, trackRow } from './codex';
import { GameOverScreen } from './gameOver';
import { Hud } from './hud';
import { claimRegion, regionLayer, removeRegionLayer } from './hudLayout';
import { LoadoutPanel } from './loadout';
import { MENU_TABS, MenuPad, menuCommandFor, type MenuCommand } from './menuPad';
import { btn, clear, el, injectStyle, MAP_KIND_GLYPH, Pool } from './style';
import { PrologueOverlay, type PrologueVM } from './prologue';
import { VendorPanel } from './vendor';
import {
  factionSwingText,
  keyOf,
  MAP_KIND_LABEL,
  MAP_ROOM_KINDS,
  pauseMenuItems,
  uiOwnsEscape,
  uiOwnsKeyboard,
  type GameVM,
  type GameOverVM,
  type Hand,
  type MapRoomKind,
  type MapVM,
  type PauseAction,
  type Screen,
  type ToastKind,
  type UICallbacks,
} from './viewModels';

const TOAST_TTL = 4.5;
const MAX_TOASTS = 6;
/** Hold this long before Tab means "radial" rather than "open the loadout". */
const RADIAL_HOLD_MS = 190;

/**
 * The shell seam.
 *
 * "Settings" and "Quit to title" are the application shell's business, not the
 * run's: they outlive the Game, and `main.ts` owns the scene machine that
 * performs them. They arrive the same way every other integration point does —
 * through `UICallbacks` — because `Game` now carries `onOpenSettings` and
 * `onQuitToTitle` beside `onPrologueDone` and forwards them here.
 *
 * `hasShellExits` is the awkward part and it is deliberate. `main.ts` assigns
 * those two fields *after* `new Game(...)` returns, so anything this file reads
 * at construction time reads null. A predicate asked when the menu is shown
 * gets the truth; a value captured when the menu is built never can. That is
 * the entire reason `buildPause` is lazy — see `ensurePause`.
 */

export interface UIRootOptions {
  /**
   * Bind Tab / Escape / C / M directly on window. Convenient standalone (and
   * for the demo harness); integrators that already route through core/input.ts
   * should pass false and drive setScreen / beginRadial themselves.
   */
  selfBindKeys?: boolean;
}

export class UIRoot {
  private host: HTMLElement;
  private cb: UICallbacks;
  private opts: UIRootOptions;

  private regions: HTMLElement;
  private modalLayer: HTMLElement;
  private overlayLayer: HTMLElement;
  private scrim: HTMLElement;

  private hud: Hud;
  private loadout: LoadoutPanel;
  private codex: CodexPanel;
  private mapPanel: HTMLElement;
  private mapBox!: HTMLElement;
  /** Built on first show, not at construction. See `ensurePause`. */
  private pausePanel: HTMLElement | null = null;
  /**
   * The postmortem. Built on the first death, for the same reason the pause
   * panel is built on the first pause: most frames of most runs never need it.
   */
  private gameOverPanel: GameOverScreen | null = null;
  /**
   * The snapshot the screen is showing, or null when nothing has died.
   *
   * Held here rather than rebuilt per frame — see `setGameOver`. It is also the
   * predicate `Game` asks before showing the screen at all.
   */
  private gameOverVM: GameOverVM | null = null;

  private screen: Screen = 'none';
  private vm: GameVM | null = null;
  private mapRev = -1;
  private pauseTracksRev = -1;

  private toastHost: HTMLElement;
  private toastPool: Pool<HTMLElement>;
  private toasts: Array<{ msg: string; kind: ToastKind; note: string; ttl: number }> = [];
  private toastsDirty = false;

  private vendor: VendorPanel;
  private prologue: PrologueOverlay;
  /** Null except during the prologue, which is once per profile (#22). */
  private prologueVM: PrologueVM | null = null;
  private radial: HTMLElement;
  private radialDisc!: HTMLElement;
  private radialHub!: HTMLElement;
  private radialItems: HTMLElement[] = [];
  private radialOpen = false;
  private radialPick = -1;
  private tabDownAt = 0;
  private tabTimer = 0;

  private listeners: Array<[EventTarget, string, EventListener, boolean]> = [];

  /**
   * The pad, while a panel is open — issue #78.
   *
   * A second, menu-scoped consumer of `navigator.getGamepads()`, live for
   * exactly as long as `Input` is deaf. See `menuPad.ts` for why this is a
   * second poll rather than a relaxation of the run-time gate, and for the
   * three bindings decisions (#78 asks for them in writing).
   */
  private menuPad = new MenuPad();
  /**
   * Which tab the shoulder buttons last moved to, per list panel.
   *
   * Local rather than read back from the panel, because neither `LoadoutPanel`
   * nor `CodexPanel` exposes its current tab and adding a getter to both to
   * serve one caller is a wider change than #78 is. The honest consequence:
   * clicking a tab with the mouse and then pressing RB jumps to *this* index
   * rather than to the one after the clicked tab. It is off by a press, never
   * unreachable, and the alternative — a fifth tab the pad cannot see — is the
   * failure that actually matters.
   */
  private padTab: Record<'loadout' | 'codex', number> = { loadout: 0, codex: 0 };

  constructor(root: HTMLElement, cb: UICallbacks = {}, opts: UIRootOptions = {}) {
    this.host = root;
    this.cb = cb;
    this.opts = opts;
    injectStyle();
    root.classList.add('ui-root');

    // Panels navigate through UIRoot; everything else passes straight through.
    const panelCb: UICallbacks = {
      ...cb,
      onScreenChange: (s) => this.setScreen(s),
      onCodexOpenRef: (ref) => {
        this.setScreen('codex');
        this.codex.openRef(ref.kind, ref.id);
        cb.onCodexOpenRef?.(ref);
      },
    };

    // ---- hud: two regions on the region layer, no layer of its own.
    //
    // `root`, not a detached wrapper. `claimRegion` resolves its host up to the
    // nearest `.ui-root`, and a host that is not attached yet resolves to
    // itself — which silently built a *second* region layer inside the old hud
    // layer and put top-left and bottom-band in it, where they overlapped
    // left-rail by 413x74. The overlap check found that within a minute of the
    // regions existing, which is the entire argument for having it.
    this.hud = new Hud(root);
    this.regions = regionLayer(root);

    // ---- modal layer
    this.modalLayer = el('div', 'ui-layer');
    this.modalLayer.style.pointerEvents = 'none';
    this.modalLayer.hidden = true;
    this.scrim = el('div', 'scrim');
    this.loadout = new LoadoutPanel(panelCb);
    this.codex = new CodexPanel(panelCb);
    this.mapPanel = this.buildMap();
    this.modalLayer.append(this.scrim, this.loadout.root, this.codex.root, this.mapPanel);
    for (const p of [this.loadout.root, this.codex.root, this.mapPanel]) p.hidden = true;

    // ---- overlay layer
    this.overlayLayer = el('div', 'ui-layer');
    this.overlayLayer.style.pointerEvents = 'none';
    this.toastHost = el('div', 'toasts');
    this.toastPool = new Pool(this.toastHost, () => {
      const t = el('div', 'toast');
      t.append(el('i'), el('span'), el('em'));
      return t;
    });
    this.radial = this.buildRadial();
    this.radial.hidden = true;
    // The vendor lives on the overlay, not the modal layer: it is an in-world
    // prompt in the altar's shape (#11), so it must never scrim, never pause and
    // never take the pointer — the player is still fighting behind it.
    this.vendor = new VendorPanel();
    this.prologue = new PrologueOverlay();
    // The radial and the prologue stay on the overlay layer: both are
    // full-bleed and *replace* the picture for as long as they are up, so they
    // have nothing to share the screen with and nothing to collide with. The
    // toast stack and the vendor stall do share it, so they claim regions.
    this.overlayLayer.append(this.radial, this.prologue.root);
    claimRegion(root, 'top-right').appendChild(this.toastHost);
    claimRegion(root, 'centre-prompt').appendChild(this.vendor.root);

    root.append(this.modalLayer, this.overlayLayer);

    if (opts.selfBindKeys !== false) this.bindKeys();
  }

  /** Drop the window listeners without tearing down the DOM. */
  releaseKeyboard(): void {
    for (const [t, type, fn, capture] of this.listeners) t.removeEventListener(type, fn, capture);
    this.listeners.length = 0;
    window.clearTimeout(this.tabTimer);
  }

  // ------------------------------------------------------------- per frame

  update(vm: GameVM, dt: number): void {
    this.vm = vm;
    // Before anything reads the screen, because a pad `back` this frame should
    // take effect this frame rather than leave one frame of a panel that is
    // already closed. `Game.render` calls this every frame *including while
    // paused*, which is what makes the pump's `dt` a real duration without a
    // clock — see `menuPad.ts`.
    this.pumpPad(dt);
    this.hud.update(vm.player);
    // Hidden while a panel is open, for the same reason toasts are: the vendor
    // is a world prompt, and a world prompt over a full-screen panel is reading
    // material for a world the player cannot currently see.
    this.vendor.update(this.screen === 'none' ? vm.vendor : null);
    // Same rule, same reason. The prologue drives from `setPrologue` rather than
    // through the frame because nothing in it is derived from the simulation —
    // it is an authored script, and routing it through `uiAdapter` would put a
    // per-frame cost on every run that is not the prologue.
    //
    // The one thing in it that is *not* authored is the key caps (#135): the
    // beats name actions, and `vm.keyLabels` is what those actions are on right
    // now. It arrives with the frame rather than with `setPrologue` because the
    // bindings are not part of the script — they change while the prologue is
    // on screen if the player rebinds — and it costs a non-prologue frame one
    // property read.
    this.prologue.update(this.screen === 'none' ? this.prologueVM : null, vm.keyLabels);

    switch (this.screen) {
      case 'loadout':
        this.loadout.update(vm);
        break;
      case 'codex':
        this.codex.update(vm);
        break;
      case 'map':
        this.updateMap(vm.map);
        break;
      case 'pause':
        this.renderPauseTracks();
        break;
      default:
        break;
    }

    if (this.radialOpen) this.updateRadial(vm);
    this.tickToasts(dt);
  }

  // ------------------------------------------------------------- screens

  setScreen(s: Screen): void {
    if (s === this.screen) return;
    this.screen = s;
    // Adopt whatever the pad is holding *right now* without acting on it. The
    // button that opened this panel is still physically down for several frames
    // and would otherwise read as a fresh press inside it — the menu would
    // flicker open and shut and the player could not pause at all. Same rule as
    // `Input.pollGamepad`'s swallowed-press guard, for the same reason.
    this.menuPad.arm();

    // Asking for the panel here is what asks `hasShellExits()` here.
    if (s === 'pause') this.ensurePause();
    if (s === 'gameover') this.ensureGameOver();

    this.loadout.root.hidden = s !== 'loadout';
    this.codex.root.hidden = s !== 'codex';
    this.mapPanel.hidden = s !== 'map';
    if (this.pausePanel) this.pausePanel.hidden = s !== 'pause';
    if (this.gameOverPanel) this.gameOverPanel.root.hidden = s !== 'gameover';

    const open = s !== 'none';
    this.modalLayer.hidden = !open;
    // The death screen takes the pointer on its own two sub-elements and
    // nowhere else, so the modal layer must stay transparent under it — a
    // layer-wide `auto` would put an invisible sheet over the live world the
    // whole screen exists to keep showing.
    this.modalLayer.style.pointerEvents = open && s !== 'gameover' ? 'auto' : 'none';
    // **The scrim is the one thing a death screen must not have.** Every other
    // panel dims the world because the world is not what you are looking at;
    // here it is exactly what you are looking at. This single line is where
    // "the sim keeps running" stops being a claim in a comment and becomes
    // something a player can see.
    this.scrim.hidden = s === 'gameover';
    // A health bar belonging to a corpse is a lie. It comes back with the next
    // run, which is the only thing it can honestly describe.
    //
    // The prologue clause is not defensive padding: `setPrologue` is the other
    // writer of this flag, and without it, opening the codex during the
    // prologue (KeyC is live there) would call this with `false` and put the
    // HUD back for the rest of the scene — nine ability chips and two weapon
    // cards on beat one, which is the exact thing #22 hid. One flag, two
    // writers, and the second must not clobber the first.
    this.hud.setHidden(s === 'gameover' || this.prologueVM !== null);
    // Toasts are combat feedback and they sit over the panel's detail column.
    // While a panel is open the same information is one click away inside it,
    // so hide them rather than let them occlude the thing being read. They keep
    // ticking, so nothing queues up and dumps on close.
    this.toastHost.hidden = open;

    if (this.vm) {
      if (s === 'loadout') {
        this.loadout.update(this.vm);
        this.loadout.onShow();
      } else if (s === 'codex') {
        this.codex.update(this.vm);
        this.codex.onShow();
      } else if (s === 'map') {
        this.mapRev = -1;
        this.updateMap(this.vm.map);
      }
    }
    if (s === 'pause') {
      this.renderPauseTracks();
      this.pausePanel?.focus();
    }
    if (s === 'gameover') this.gameOverPanel?.onShow();

    // Report, never decide. Pause policy belongs to the integrator.
    this.cb.onScreenChange?.(s);
  }

  get currentScreen(): Screen {
    return this.screen;
  }

  /**
   * Drive the prologue overlay (#22). Null ends it — and ending it is a single
   * call, which is what keeps "skipped" and "finished" from being two code paths
   * that each have to remember to clean up.
   */
  setPrologue(vm: PrologueVM | null): void {
    this.prologueVM = vm;
    // The prologue teaches with a key, not a sentence (docs/08 §2). Nine
    // ability chips and two full weapon cards on beat one are the sentence —
    // they name everything at once, which is the opposite of teaching. The HUD
    // comes back with the run, where it is earned.
    this.hud.setHidden(vm !== null);
  }

  /** Biome grading hook: overwrite any of the palette custom properties. */
  setTint(vars: Record<string, string>): void {
    for (const k in vars) this.host.style.setProperty(k, vars[k]);
  }

  // ------------------------------------------------------------- toasts

  /**
   * `note` is a second, quieter line under the headline. It exists because some
   * events have a consequence worth a sentence — a biome's ambient hazard, say
   * — and folding that into `msg` makes a toast nobody finishes reading.
   */
  toast(msg: string, kind: ToastKind = 'discovery', note = ''): void {
    this.toasts.push({ msg, kind, note, ttl: TOAST_TTL });
    if (this.toasts.length > MAX_TOASTS) this.toasts.shift();
    this.toastsDirty = true;
  }

  /**
   * A deed moved reputation — #11.
   *
   * The codex tab is where standing is *studied*; this is where it is *seen
   * happening*, and the acceptance criterion is the second one: pleasing one
   * faction has to be visibly the same event as displeasing another. So the note
   * always carries both halves, and a swing that moved nothing produces no toast
   * rather than a toast that says nothing.
   */
  factionToast(deedName: string, gainedName: string, lostName: string, delta: number): void {
    const swing = factionSwingText(gainedName, lostName, delta);
    if (!swing) return;
    this.toast(deedName, 'discovery', swing);
  }

  private tickToasts(dt: number): void {
    for (let i = this.toasts.length - 1; i >= 0; i--) {
      this.toasts[i].ttl -= dt;
      if (this.toasts[i].ttl <= 0) {
        this.toasts.splice(i, 1);
        this.toastsDirty = true;
      }
    }
    if (!this.toastsDirty) return;
    this.toastsDirty = false;

    this.toastPool.begin();
    for (const t of this.toasts) {
      const e = this.toastPool.next();
      if (e.dataset.kind !== t.kind) e.dataset.kind = t.kind;
      const label = t.kind === 'warn' ? 'warning' : t.kind;
      const head = e.children[0] as HTMLElement;
      if (head.textContent !== label) head.textContent = label;
      const body = e.children[1] as HTMLElement;
      if (body.textContent !== t.msg) body.textContent = t.msg;
      const note = e.children[2] as HTMLElement;
      if (note.textContent !== t.note) note.textContent = t.note;
      if (note.hidden === (t.note !== '')) note.hidden = t.note === '';
    }
    this.toastPool.end();
  }

  // ------------------------------------------------------------- map

  private buildMap(): HTMLElement {
    const p = el('div', 'panel');
    p.tabIndex = -1;
    const head = el('div', 'panel__head');
    head.append(el('div', 'panel__title', 'Map'), el('div', 'ui-grow'), btn('✕', 'close'));
    head.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('[data-act="close"]')) this.setScreen('none');
    });
    this.mapBox = el('div', 'map');
    const foot = el('div', 'panel__foot');
    foot.append(el('span', '', 'Visited rooms are solid; the rest are inferred from exits.'));
    p.append(head, this.mapBox, foot);
    return p;
  }

  private updateMap(map: MapVM | null): void {
    if (!map) {
      if (this.mapRev !== -2) {
        this.mapRev = -2;
        clear(this.mapBox);
        this.mapBox.append(el('div', 'detail__line', '  no map data'));
      }
      return;
    }
    if (map.revision === this.mapRev) return;
    this.mapRev = map.revision;
    clear(this.mapBox);

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const r of map.rooms) {
      if (r.x < minX) minX = r.x;
      if (r.y < minY) minY = r.y;
      if (r.x + r.w > maxX) maxX = r.x + r.w;
      if (r.y + r.h > maxY) maxY = r.y + r.h;
    }
    const w = Math.max(1, maxX - minX);
    const h = Math.max(1, maxY - minY);

    const frag = document.createDocumentFragment();
    /** Which kinds are actually on screen — the legend below shows only these. */
    const kindsSeen = new Set<MapRoomKind>();
    for (const r of map.rooms) {
      const d = el('div', 'room');
      d.style.left = ((r.x - minX) / w) * 100 + '%';
      d.style.top = ((r.y - minY) / h) * 100 + '%';
      d.style.width = (r.w / w) * 100 + '%';
      d.style.height = (r.h / h) * 100 + '%';
      d.dataset.visited = r.visited ? '1' : '0';
      d.dataset.current = r.current ? '1' : '0';
      d.dataset.kind = r.kind;
      // The tooltip is the long form of the glyph. `MAP_KIND_LABEL` is empty for
      // an ordinary room, so an ordinary room says only where it is.
      const label = MAP_KIND_LABEL[r.kind];
      d.title = label ? r.biome + ' · ' + label : r.biome;
      kindsSeen.add(r.kind);
      frag.append(d);
    }
    this.mapBox.append(frag);
    const legend = el('div', 'panel__foot map__legend');
    legend.append(el('span', '', map.biomeName + ' · ' + map.mode + ' · ' + map.rooms.length + ' rooms'));
    // A key for the colours, and only for the kinds this map actually contains.
    // A legend listing a kind that is not on screen sends the player looking for
    // a room that is not there, which is the same failure as drawing one.
    for (const k of MAP_ROOM_KINDS) {
      if (k === 'normal' || !kindsSeen.has(k)) continue;
      const key = el('span', 'map__key');
      key.dataset.kind = k;
      key.append(el('i', '', MAP_KIND_GLYPH[k]), el('span', '', MAP_KIND_LABEL[k]));
      legend.append(key);
    }
    // The arrival toast is where the hazard is first read; this is where the
    // player goes back to re-read it, which a 4.5-second toast cannot serve.
    if (map.hazardText) legend.append(el('span', 'ui-warn', map.hazardText));
    this.mapBox.append(legend);
  }

  // ------------------------------------------------------------- pause

  /**
   * Build the pause box the first time it is shown, and only then.
   *
   * The lateness is the point. `main.ts` constructs a `Game` and assigns
   * `onOpenSettings` / `onQuitToTitle` on the line after, so a pause box built
   * during `new UIRoot(...)` would ask `hasShellExits()` while both are still
   * null and would omit both rows for the life of the run — a bug with no
   * symptom other than two menu entries that never appear.
   */
  private ensurePause(): HTMLElement {
    if (this.pausePanel) return this.pausePanel;
    const p = this.buildPause();
    p.hidden = true;
    this.modalLayer.append(p);
    this.pausePanel = p;
    return p;
  }

  private ensureGameOver(): GameOverScreen {
    if (this.gameOverPanel) return this.gameOverPanel;
    const g = new GameOverScreen({
      // Both exits leave through the integrator, and `continue` has its own
      // callback rather than reusing `onQuit` — see `onContinueAfterRun` in
      // `viewModels.ts` for why sharing it would bank the run twice.
      onChoose: (a) => {
        this.setScreen('none');
        // The snapshot goes with the screen. A stale postmortem left loaded is
        // a `hasGameOver` that answers yes about a run two runs ago.
        this.gameOverVM = null;
        if (a === 'continue') this.cb.onContinueAfterRun?.();
        else this.cb.onQuitToTitle?.();
      },
    });
    g.root.hidden = true;
    this.modalLayer.append(g.root);
    this.gameOverPanel = g;
    if (this.gameOverVM) g.update(this.gameOverVM);
    return g;
  }

  /**
   * Hand the screen its postmortem — **called once, when the run ends.**
   *
   * Not a per-frame push, and the difference is the whole performance story of
   * this feature. The frame budget's slack is 0.3 ms (#75) and a simulation
   * that keeps running behind this screen already costs what it always cost, so
   * the overlay's own budget is *zero*: `update(dt)` above has no `gameover`
   * case, the panel has no revision counter to compare, and nothing in
   * `ui/gameOver.ts` runs again between this call and the next run.
   *
   * Passing null clears the snapshot, which is what the next run's start does.
   */
  setGameOver(vm: GameOverVM | null): void {
    this.gameOverVM = vm;
    if (vm) this.ensureGameOver().update(vm);
  }

  /** Is a postmortem loaded? `Game` asks before showing the screen. */
  get hasGameOver(): boolean {
    return this.gameOverVM !== null;
  }

  private buildPause(): HTMLElement {
    const p = el('div', 'pause');
    p.tabIndex = -1;
    const box = el('div', 'pause__box');
    const h = document.createElement('h1');
    h.textContent = 'Paused';
    box.append(h);

    // `pauseMenuItems` decides *which* entries exist; this decides what each
    // one does. The two shell entries are only offered when the integrator has
    // something behind them, so there is never a row here that does nothing.
    const run: Record<PauseAction, () => void> = {
      resume: () => {
        this.setScreen('none');
        this.cb.onResume?.();
      },
      loadout: () => this.setScreen('loadout'),
      codex: () => this.setScreen('codex'),
      map: () => this.setScreen('map'),
      settings: () => this.cb.onOpenSettings?.(),
      quitToTitle: () => this.cb.onQuitToTitle?.(),
      abandon: () => this.cb.onQuit?.(),
    };

    for (const item of pauseMenuItems(
      this.cb.hasShellExits?.() ?? { settings: false, quitToTitle: false },
    )) {
      const b = btn(item.label, item.id);
      b.addEventListener('click', run[item.id]);
      box.append(b);
    }

    // The five meta tracks are the reason to keep playing; showing them on the
    // pause screen is free and it is where players actually look.
    const tracks = el('div');
    tracks.dataset.tracks = '1';
    box.append(tracks);
    p.append(box);
    return p;
  }

  /**
   * Called on entry *and* every frame while paused: a screen may legitimately
   * be opened before the first update() lands (boot straight into pause), and
   * an entry-only render would then leave it permanently blank. The revision
   * check keeps the steady-state cost at one integer compare.
   */
  private renderPauseTracks(): void {
    const host = this.pausePanel?.querySelector<HTMLElement>('[data-tracks]');
    if (!host || !this.vm) return;
    if (this.pauseTracksRev === this.vm.progression.revision) return;
    this.pauseTracksRev = this.vm.progression.revision;
    clear(host);
    for (const t of this.vm.progression.tracks) host.append(trackRow(t));
  }

  // ------------------------------------------------------------- radial

  private buildRadial(): HTMLElement {
    const layer = el('div', 'radial');
    this.radialDisc = el('div', 'radial__disc');
    this.radialHub = el('div', 'radial__hub');
    this.radialDisc.append(this.radialHub);
    layer.append(this.radialDisc);

    // Slots are fixed at 8 (docs/04 §7), so the wheel is built once.
    for (let i = 0; i < 8; i++) {
      const item = el('div', 'radial__item');
      const a = (i / 8) * Math.PI * 2 - Math.PI / 2;
      item.style.left = 50 + Math.cos(a) * 36 + '%';
      item.style.top = 50 + Math.sin(a) * 36 + '%';
      this.radialItems.push(item);
      this.radialDisc.append(item);
    }

    layer.addEventListener('mousemove', (e) => {
      const r = this.radialDisc.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      if (Math.hypot(dx, dy) < r.width * 0.16) {
        this.radialPick = -1;
      } else {
        const a = Math.atan2(dy, dx) + Math.PI / 2;
        const idx = Math.round((a / (Math.PI * 2)) * 8 + 8) % 8;
        this.radialPick = idx;
      }
      this.paintRadial();
    });
    // Both buttons commit, and the right one commits to the off hand — the
    // radial is a dual-wield swap, so a wheel that can only ever fill the main
    // hand can express half of every loadout the game is about.
    layer.addEventListener('mousedown', (e) =>
      this.endRadial(true, e.button === 2 || e.shiftKey ? 'off' : 'main'),
    );
    layer.addEventListener('contextmenu', (e) => e.preventDefault());
    return layer;
  }

  /** Hold-Tab quick swap. Public so an integrator can drive it from Input. */
  beginRadial(): void {
    if (this.radialOpen || this.screen !== 'none') return;
    this.radialOpen = true;
    this.radialPick = -1;
    this.radial.hidden = false;
    this.overlayLayer.style.pointerEvents = 'auto';
    if (this.vm) this.updateRadial(this.vm);
  }

  /**
   * Close the wheel. `hand` is which hand the picked wedge goes to — it used to
   * be hard-coded to 'main', so seven of the eight wedges could be selected and
   * every one of them did the same thing.
   */
  endRadial(commit: boolean, hand: Hand = 'main'): void {
    if (!this.radialOpen) return;
    this.radialOpen = false;
    this.radial.hidden = true;
    this.overlayLayer.style.pointerEvents = 'none';
    if (!commit || this.radialPick < 0 || !this.vm) return;
    const id = this.vm.inventory.slots[this.radialPick];
    if (!id) return;
    // Picking the weapon already in that hand is a no-op, not a re-equip:
    // re-equipping cancels the current node and resets the combo.
    if (this.vm.inventory.hands[hand] === id) return;
    this.cb.onEquip?.(id, hand);
  }

  /** Alternate the two saved pairs — docs/04 §7, "two saved pairs, one hotkey". */
  cyclePair(): void {
    const inv = this.vm?.inventory;
    if (!inv || inv.pairs.length === 0) return;
    const active = inv.pairs.findIndex((p) => p.active);
    const next = (active + 1) % inv.pairs.length;
    this.cb.onSelectPair?.(inv.pairs[next].index);
  }

  private updateRadial(vm: GameVM): void {
    const inv = vm.inventory;
    for (let i = 0; i < 8; i++) {
      const id = inv.slots[i] ?? null;
      // Slots hold instance ids (#12) — matching on the definition would put
      // the wrong roll of a duplicated weapon in the wedge.
      const w = id ? inv.weapons.find((x) => keyOf(x) === id) : null;
      const item = this.radialItems[i];
      // Marking the hands inside the wheel is what makes a swap a decision:
      // otherwise two of the eight wedges silently do nothing.
      const worn =
        id && inv.hands.main === id ? '⟨M⟩ ' : id && inv.hands.off === id ? '⟨O⟩ ' : '';
      const label = w ? worn + w.glyph + ' ' + w.name : '—';
      if (item.textContent !== label) item.textContent = label;
      const empty = w ? '0' : '1';
      if (item.dataset.empty !== empty) item.dataset.empty = empty;
    }
    const pair = inv.pairs.find((p) => p.active);
    const hub =
      'QUICK SWAP\n' +
      (pair ? pair.name : 'no pair') +
      '\nrelease Tab → main hand\nhold shift → off hand';
    if (this.radialHub.textContent !== hub) {
      this.radialHub.textContent = hub;
      this.radialHub.style.whiteSpace = 'pre-line';
    }
    this.paintRadial();
  }

  private paintRadial(): void {
    for (let i = 0; i < 8; i++) {
      const on = i === this.radialPick ? '1' : '0';
      if (this.radialItems[i].dataset.on !== on) this.radialItems[i].dataset.on = on;
    }
  }

  // ------------------------------------------------------------- gamepad
  //
  // Issue #78. The keyboard bindings below are the other half of this: they are
  // what a pad had nothing equivalent to, and they are deliberately left
  // untouched. A pad does not synthesise key events — a synthesised
  // `KeyboardEvent` is `isTrusted: false`, so it does not activate a focused
  // control and buys nothing a plain record does not — it produces *intents*,
  // `menuCommandFor` turns those into commands, and the six cases below are the
  // only DOM this feature contains.

  /**
   * One frame of the menu pad. Inert unless a panel is open.
   *
   * The guard is load-bearing and is not an optimisation: with no panel open
   * `Input` is enabled and owns the pad, and a second reader of the same
   * physical press is the duplication #67 spent four defects on. Opening a
   * panel from a live run therefore belongs to `Game`, through the `inventory`
   * / `map` / `pause` actions `DEFAULT_PAD_BINDINGS` already produces — see
   * `PAD_OPENS` in `menuPad.ts`, which records the seam from this side.
   */
  private pumpPad(dt: number): void {
    if (this.screen === 'none') return;
    for (const intent of this.menuPad.poll(dt)) {
      // Re-asked per intent rather than hoisted: a `back` earlier in the same
      // frame has already changed the screen, and the next intent belongs to
      // wherever the player now is.
      const cmd = menuCommandFor(intent, this.screen);
      if (cmd) this.runMenuCommand(cmd);
    }
  }

  private runMenuCommand(c: MenuCommand): void {
    switch (c.kind) {
      case 'screen':
        this.setScreen(c.to);
        return;
      case 'resume':
        // Exactly what the pause menu's own Resume row does, both halves. The
        // integrator is told separately from the screen change because pause
        // policy is its business, not this file's.
        this.setScreen('none');
        this.cb.onResume?.();
        return;
      case 'key':
        this.sendPanelKey(c.key, c.shift);
        return;
      case 'focus':
        this.movePanelFocus(c.delta);
        return;
      case 'activate':
        this.activateFocused();
        return;
      case 'tab':
        this.cyclePanelTab(c.delta);
        return;
    }
  }

  /**
   * Hand a keystroke to the open list panel's existing `handleKey`.
   *
   * A plain record cast to `KeyboardEvent` rather than `new KeyboardEvent(...)`:
   * the panels read `e.key` and `e.shiftKey` and nothing else, a constructed
   * event would be untrusted anyway, and building one would make this method
   * need a DOM constructor it otherwise does not.
   */
  private sendPanelKey(key: string, shift: boolean): void {
    const e = {
      key,
      code: key,
      shiftKey: shift,
      repeat: false,
      preventDefault: () => {},
      stopPropagation: () => {},
    } as unknown as KeyboardEvent;
    if (this.screen === 'loadout') this.loadout.handleKey(e);
    else if (this.screen === 'codex') this.codex.handleKey(e);
  }

  /** The buttons a pad can move between on the current panel, in visual order. */
  private focusables(): HTMLElement[] {
    const host =
      this.screen === 'pause'
        ? this.pausePanel
        : this.screen === 'gameover'
          ? (this.gameOverPanel?.root ?? null)
          : null;
    if (!host) return [];
    // `closest('[hidden]')` rather than `!b.hidden`, because the death screen
    // hides a whole *phase* rather than individual rows: during phase one the
    // action buttons exist, are not themselves `hidden`, and are inside a
    // hidden column. Without this the pad's focus ring would walk onto
    // "Continue" while nothing but two lines of text is on screen, and A would
    // end the run from a control the player cannot see.
    return [...host.querySelectorAll<HTMLElement>('button')].filter(
      (b) => b.closest('[hidden]') === null,
    );
  }

  private movePanelFocus(delta: number): void {
    const items = this.focusables();
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    // Wrapping. `setScreen('pause')` focuses the *panel*, not a row, so `at` is
    // -1 on the first press and the ring has to start somewhere sensible:
    // pressing down lands on the first row and pressing up on the last.
    const next = at < 0 ? (delta > 0 ? 0 : items.length - 1) : (at + delta + items.length) % items.length;
    items[next].focus();
  }

  private activateFocused(): void {
    const items = this.focusables();
    if (items.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    if (active && items.includes(active)) {
      active.click();
      return;
    }
    // Confirm with no cursor yet places one instead of doing nothing. The very
    // first press after the menu opens is otherwise silently eaten, which reads
    // as the pad not working — the whole complaint #78 is.
    this.movePanelFocus(1);
  }

  /**
   * The shoulder buttons, cycling the open list panel's tab strip.
   *
   * Both panels already select a tab by the number keys `1`–`n`, so this is the
   * existing mechanism reached by a button. `padTab` is why the count lives in
   * `MENU_TABS` rather than as a literal here.
   */
  private cyclePanelTab(delta: number): void {
    const which =
      this.screen === 'loadout' ? 'loadout' : this.screen === 'codex' ? 'codex' : null;
    if (!which) return;
    const n = MENU_TABS[which];
    this.padTab[which] = (this.padTab[which] + delta + n) % n;
    this.sendPanelKey(String(this.padTab[which] + 1), false);
  }

  // ------------------------------------------------------------- keyboard

  private on(t: EventTarget, type: string, fn: EventListener, capture = false): void {
    t.addEventListener(type, fn, capture);
    this.listeners.push([t, type, fn, capture]);
  }

  /**
   * Capture phase, so that while a panel is open the game's own window-level
   * input listener never sees the keystroke. A panel that lets WASD leak into
   * the player controller is a panel that gets you killed.
   */
  /**
   * Read the focus and ask `uiOwnsKeyboard`. Kept here because this is the only
   * file that knows which elements are the run UI's own.
   */
  private ownsKeyboard(): boolean {
    const active = document.activeElement;
    const focused = active !== null && active !== document.body;
    return uiOwnsKeyboard({
      focused,
      inOwnLayers:
        focused &&
        (this.regions.contains(active) ||
          this.modalLayer.contains(active) ||
          this.overlayLayer.contains(active)),
      inSharedHost: focused && this.host.contains(active),
    });
  }

  private bindKeys(): void {
    this.on(
      window,
      'keydown',
      ((e: KeyboardEvent) => {
        if (!this.ownsKeyboard()) return;
        if (e.code === 'Tab') {
          e.preventDefault();
          if (this.screen !== 'none') {
            // Tab inside a panel is plain focus navigation; leave it alone
            // except for the game's binding.
            e.stopPropagation();
            return;
          }
          if (!e.repeat) {
            this.tabDownAt = performance.now();
            this.tabTimer = window.setTimeout(() => this.beginRadial(), RADIAL_HOLD_MS);
          }
          e.stopPropagation();
          return;
        }

        if (this.screen === 'none') {
          // #82: Escape is the one key the game also has a claim on — the
          // prologue's hold-to-skip is bound to it. Yield without touching the
          // event so `Input`'s bubble-phase listener actually receives it;
          // swallowing it here is what made the hold unreachable from a
          // keyboard for as long as this handler has existed.
          if (
            e.code === 'Escape' &&
            !uiOwnsEscape({
              screen: this.screen,
              prologueSkippable: this.prologueVM?.skippable === true,
            })
          ) {
            return;
          }
          if (e.code === 'KeyC') this.setScreen('codex');
          else if (e.code === 'KeyM') this.setScreen('map');
          else if (e.code === 'Escape') this.setScreen('pause');
          else return;
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // A screen is open: the UI owns the keyboard.
        e.stopPropagation();
        // **The death screen is the one panel Escape does not close**, and the
        // check is here rather than inside the panel because "unhandled key"
        // and "close me" are the same branch below. The run is over: there is
        // nothing behind this screen to go back to, and dismissing it would
        // leave a dead player standing in a live world with no way to restart.
        if (e.code === 'Escape' && this.screen !== 'gameover') {
          e.preventDefault();
          this.setScreen('none');
          return;
        }
        const handled =
          this.screen === 'loadout'
            ? this.loadout.handleKey(e)
            : this.screen === 'codex'
              ? this.codex.handleKey(e)
              : this.screen === 'gameover'
                ? (this.gameOverPanel?.handleKey(e) ?? false)
                : false;
        if (handled) e.preventDefault();
      }) as EventListener,
      true,
    );

    this.on(
      window,
      'keyup',
      ((e: KeyboardEvent) => {
        if (e.code !== 'Tab' || !this.ownsKeyboard()) return;
        window.clearTimeout(this.tabTimer);
        // Tab taps toggle the loadout, and from the death screen that would
        // open an inventory belonging to a run that has already been banked.
        // Swallowed rather than ignored, so it does not fall through to the
        // player controller either.
        if (this.screen === 'gameover') {
          e.stopPropagation();
          return;
        }
        if (this.radialOpen) {
          this.endRadial(true, e.shiftKey ? 'off' : 'main');
        } else if (performance.now() - this.tabDownAt < RADIAL_HOLD_MS) {
          // A tap opens the panel; a hold was the radial.
          this.setScreen(this.screen === 'loadout' ? 'none' : 'loadout');
        }
        e.stopPropagation();
      }) as EventListener,
      true,
    );

    // Losing focus mid-hold would otherwise leave the radial stuck open.
    this.on(window, 'blur', () => {
      window.clearTimeout(this.tabTimer);
      this.endRadial(false);
    });
  }

  // ------------------------------------------------------------- teardown

  destroy(): void {
    this.releaseKeyboard();
    // No listener and no timer to drop — the pump is driven by the `dt` it is
    // handed, which is the whole argument for that design over a repeat timer.
    // Reset anyway, so a pad button held across a teardown cannot be seen as
    // still-held by a `UIRoot` that replaces this one.
    this.menuPad.reset();
    this.hud.destroy();
    this.loadout.destroy();
    this.codex.destroy();
    this.gameOverPanel?.destroy();
    this.toastPool.dispose();
    this.modalLayer.remove();
    this.overlayLayer.remove();
    // The region layer goes too, and with it anything the shell mounted into
    // it. `mountRunOverlays`' own dispose still runs and still removes its
    // elements; removing an already-detached node is a no-op.
    removeRegionLayer(this.host);
    this.host.classList.remove('ui-root');
    // The stylesheet is shared and idempotent — a second UIRoot may still be
    // mounted, so it is deliberately left in place.
  }
}
